/**
 * «Юниверситет» (28.09, ТЗ владельца): учебная мини-RPG филиала — движок и вид как в макете
 * «Юниверситет — макет.html», данные настоящие: пакет, подписи медиа и прогресс — ручки
 * /api/uni/:pack*, грейд и игры — /api/grades/my*. НЕ делаем (ТЗ п. 7): режимы «Наставник»
 * и «ОО», «Акцепты», ставку за игру, пороги и полоски до следующего грейда.
 *
 * Машина: главная → шторка комнаты (закрытая — «Понятно») → прогон квеста → итог → комната;
 * «Вопросы дня» → прогон → итог. Тап мимо шторки — домой: незаконченный квест не сохраняется,
 * ответы дня уже сохранены поштучно. Итог квеста записывается сразу по «Итог».
 */
import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import {
  achEarned, currentRoom, gradeRoman, passPercent, questDone, questItems, questPassed, roomStates, xpOf,
  type UniQuest, type UniQuestRec, type UniTrack,
} from "@ub/shared/uni";
import { useAuth } from "../../../auth";
import { SheetDialog } from "../../../components/shared";
import { Achievements } from "./Achievements";
import { DailyCard } from "./DailyCard";
import { Hero } from "./Hero";
import { MapCard } from "./MapSvg";
import { DailyResult, QuestResult } from "./ResultView";
import { RoomSheet } from "./RoomSheet";
import { Runner } from "./Runner";
import { TrackPlayerProvider } from "./TrackPlayer";
import { UniMediaCtx, lsGet, lsSet } from "./ctx";
import { useToasts } from "./useToasts";
import { useUniMedia } from "./useUniMedia";
import { useUniPack } from "./useUniPack";
import { useUniProgress } from "./useUniProgress";
import { useUniStats } from "./useUniStats";
import css from "./uni.module.css";

export interface UniGameProps {
  /** id учебного пакета филиала (branches.uni_pack), например «psih» */
  pack: string;
  /** квесты этого филиала — для грейда и числа игр в роли */
  branchQuestIds: string[];
  branchName: string;
  /** версия загруженного пакета — в замечания (16.10), чтобы правка знала, к какой сборке они */
  onVersion?: (v: number | null) => void;
}

const NO_TRACKS: UniTrack[] = [];

type Sheet =
  | { t: "room"; id: string }
  | { t: "quest"; room: string; q: UniQuest; title: string; ids: string[] }
  | { t: "daily"; date: string; ids: string[]; numbers: number[] };

export function UniGame({ pack: packId, branchQuestIds, branchName, onVersion }: UniGameProps) {
  const { user } = useAuth();
  const { pack, error, reload } = useUniPack(packId);
  const url = useUniMedia(packId);
  const pr = useUniProgress(packId, pack, user?.staffId ?? "");
  const stats = useUniStats(branchQuestIds);
  const toasts = useToasts();
  const push = toasts.push;
  const [sheet, setSheet] = useState<Sheet | null>(null);
  const [open, setOpen] = useState(false);
  const [nonce, setNonce] = useState(0);
  const bodyRef = useRef<HTMLDivElement>(null);

  const show = useCallback((s: Sheet) => { setSheet(s); setOpen(true); setNonce((n) => n + 1); }, []);
  const close = useCallback(() => setOpen(false), []);
  /* новое задание/экран — к началу шторки, фокус внутрь неё (иначе Tab уходит из шита) */
  const toTop = useCallback(() => {
    bodyRef.current?.scrollTo({ top: 0 });
    bodyRef.current?.focus({ preventScroll: true });
  }, []);
  useEffect(() => { if (open) bodyRef.current?.focus({ preventScroll: true }); }, [nonce, open]);
  const ver = pack ? pack.version : null;
  useEffect(() => { onVersion?.(ver); }, [ver, onVersion]);
  useEffect(() => () => onVersion?.(null), [onVersion]);   // ушли с филиала (Grades, другой филиал) — версии нет

  /* Первый вход — подсказка, куда жать (флаг — на устройстве). */
  const { ready, get } = pr;
  useEffect(() => {
    if (!pack || !ready) return;
    const key = `ub_uni_seen_${packId}`;
    if (lsGet(key)) return;
    lsSet(key, "1");
    const cur = currentRoom(pack, roomStates(pack, get()));
    if (cur) push(`Начни с комнаты «${cur.name}» на карте`, 700);
  }, [pack, ready, packId, get, push]);

  if (error) {
    return (
      <div className={css.state}>
        <p>{error}</p>
        <button type="button" className={`${css.btn} ${css.ghost}`} onClick={reload}>Повторить</button>
      </div>
    );
  }
  if (!pack || !pr.ready) return <div className={css.state}>Загружаем «Юниверситет» · {branchName}…</div>;

  const prog = pr.prog;
  const cls = prog.cls ?? pack.classes[0]?.id ?? "";
  const states = roomStates(pack, prog);
  const cur = currentRoom(pack, states);
  const roman = stats.grade ? gradeRoman(stats.grade) : null;
  const dailyIds = pr.daily.filter((id) => !!pack.items[id]);
  const todayAns = prog.daily[pr.today] ?? {};

  const announce = (fresh: string[]) => {
    for (const id of fresh) {
      const a = pack.achievements.find((x) => x.id === id);
      if (a) push(`Ачивка: ${a.title}`, 900);
    }
  };

  /* «Меломан» — разные файлы, после успешного play() */
  const onPlayed = (file: string) => {
    if (pr.get().tracks.includes(file)) return;
    announce(pr.commit((p) => (p.tracks.includes(file) ? p : { ...p, tracks: [...p.tracks, file] })));
  };

  const setClass = (id: string) => { pr.commit((p) => ({ ...p, cls: id, cls_at: new Date().toISOString() })); };

  const startQuest = (roomId: string, q: UniQuest) => {
    const { title, items } = questItems(q, cls, pack);
    show({ t: "quest", room: roomId, q, title, ids: items });
  };

  /* XP за квест — один раз; лучший результат и «без ошибок» — при каждом прохождении. */
  const finishQuest = (q: UniQuest, title: string, got: number, n: number) => {
    const passed = questPassed(got, n, passPercent(q, pack));
    const was = questDone(pr.get(), q.id);
    const now = new Date().toISOString();
    const fresh = pr.commit((p) => {
      const x = p.quests[q.id];
      const doneAt = x?.done_at ?? (passed ? now : undefined);
      const rec: UniQuestRec = {
        best: Math.max(x?.best ?? 0, got),
        of: !x || got > x.best ? n : x.of,
        ...(x?.perfect || (n > 0 && got === n) ? { perfect: true } : {}),
        ...(doneAt ? { done_at: doneAt } : {}),
      };
      return { ...p, quests: { ...p.quests, [q.id]: rec } };
    });
    if (passed && !was) push(`+${q.xp} XP · ${title}`);
    announce(fresh);
  };

  /* Ответ дня — один раз (первая запись), +daily.xp за верный. */
  const answerDaily = (date: string, id: string, ok: boolean) => {
    if (pr.get().daily[date]?.[id] !== undefined) return;
    const fresh = pr.commit((p) => ({ ...p, daily: { ...p.daily, [date]: { ...(p.daily[date] ?? {}), [id]: ok ? 1 : 0 } } }));
    if (ok) push(`+${pack.daily.xp} XP · вопрос дня`);
    announce(fresh);
  };

  const startDaily = () => {
    const rest = dailyIds.filter((id) => todayAns[id] === undefined);
    if (rest.length) show({ t: "daily", date: pr.today, ids: rest, numbers: rest.map((id) => dailyIds.indexOf(id) + 1) });
  };

  let body: ReactNode = null;
  let label = "";
  let fb = ["", ""];   // 16.10 замечания: якорь шторки и её имя в пути («Комната «Шкафы» › Квест «Двери»»)
  if (sheet?.t === "room") {
    const room = pack.rooms.find((r) => r.id === sheet.id);
    if (room) {
      label = room.name;
      fb = [`room:${room.id}`, `Комната «${room.name}»`];
      body = (
        <RoomSheet pack={pack} room={room} state={states[room.id] ?? "lock"} prog={prog}
          onQuest={(q) => startQuest(room.id, q)} onClose={close} />
      );
    }
  } else if (sheet?.t === "quest") {
    const s = sheet;
    const pct = passPercent(s.q, pack);
    label = s.title;
    fb = [`quest:${s.q.id}`, `Комната «${pack.rooms.find((r) => r.id === s.room)?.name ?? s.room}» › Квест «${s.title}»`];
    body = (
      <Runner pack={pack} title={s.title} ids={s.ids} onTop={toTop}
        onEnd={(got, n) => finishQuest(s.q, s.title, got, n)}
        result={(got, n) => (
          <QuestResult title={s.title} got={got} n={n} pct={pct} passed={questPassed(got, n, pct)}
            onBack={() => show({ t: "room", id: s.room })} onRetry={() => show({ ...s })} />
        )} />
    );
  } else if (sheet?.t === "daily") {
    const s = sheet;
    label = "Вопросы дня";
    fb = ["daily", "Вопросы дня"];
    body = (
      <Runner pack={pack} title="Вопросы дня" ids={s.ids} numbers={s.numbers} total={dailyIds.length} onTop={toTop}
        onAnswer={(id, ok) => answerDaily(s.date, id, ok)}
        result={() => (
          <DailyResult right={dailyIds.filter((id) => prog.daily[s.date]?.[id] === 1).length} n={dailyIds.length}
            xpPer={pack.daily.xp} onDone={close} />
        )} />
    );
  }

  return (
    <UniMediaCtx.Provider value={url}>
      <TrackPlayerProvider tracks={pack.tracks ?? NO_TRACKS} onPlayed={onPlayed}>
        <div className={css.root}>
          {pr.offline ? (
            <p className={css.offline} role="status">
              Нет связи с сервером — прогресс сохраняется на этом устройстве и уйдёт, когда появится сеть.
            </p>
          ) : null}
          <Hero pack={pack} stats={stats} badgeUrl={url(roman ? pack.badges?.[roman] : undefined)} cls={cls}
            xp={xpOf(pack, prog)} curName={cur?.name ?? null} onClass={setClass} />
          <DailyCard ids={dailyIds} answers={todayAns} xpPer={pack.daily.xp} onStart={startDaily} />
          <MapCard pack={pack} states={states} onOpen={(id) => show({ t: "room", id })} />
          <Achievements pack={pack} prog={prog} earned={achEarned(pack, prog)} />
        </div>
        <SheetDialog open={open} onDismiss={close} maxHeight="88dvh" ariaLabel={label || undefined} dragHandle>
          <div ref={bodyRef} key={nonce} className={css.sheetBody} tabIndex={-1} data-fb={fb[0] || undefined} data-fb-name={fb[1] || undefined}>{body}</div>
        </SheetDialog>
        {toasts.node}
      </TrackPlayerProvider>
    </UniMediaCtx.Provider>
  );
}
